import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CustomNavigationMobileWrapper,
  CustomNavigationWrapper,
} from './styles';
import NavItem, { showNavItemLabel } from './Item';
import { breakpoints, useBreakpoint } from 'app/spacing';
import useHeightMeasurement from 'hooks/useHeightMeasurement';
import { serviceLogoutStart } from 'store/authentication/authenticationAction';
import { DEFAULT_ITEM_IDS } from 'pages/Account/pages/Toolbar/constants';
import { hasMultipleBusinesses } from 'store/authentication/helpers';
import { RECENT_ITEMS } from 'queries/query-keys';
import ToolbarService from 'services/ToolbarService';
import { getTeamMemberNameAndImage } from 'store/authentication/selectors';
import { postLogOutMessage } from 'store/authentication/channels';

const NO_VALUE = '—';

const CustomNavigation = ({
  layout,
  readOnly,
  expanded,
  parentWidth,
  loading,
}) => {
  const isMobile = useBreakpoint(breakpoints.md);
  const instanceId = useSelector((s) => s.authentication.instanceId);

  const dynamicItemConfigs = useMemo(() => {
    return layout.reduce((acc, element) => {
      if (element.dynamicallyPopulated) {
        const objects = (element.config?.objects || [])
          .map((o) => o.value)
          .sort();
        const pageSize = element.config?.count || 10;

        acc[element.id] = {
          id: element.id,
          queryKey: RECENT_ITEMS.LIST(objects, pageSize),
          queryFn: async () =>
            ToolbarService.getRecentItems({ pageSize, objects }),
        };
      }

      return acc;
    }, {});
  }, [layout]);

  const spacings = useMemo(() => {
    return layout.reduce((acc, item, i) => {
      if (i === 0) {
        acc.push(40);
        return acc;
      }

      const margin = showNavItemLabel(item) ? 40 : 20;
      const previous = showNavItemLabel(layout[i - 1]) ? 40 : 20;

      acc.push(previous < margin ? previous : margin);

      return acc;
    }, []);
  }, [layout]);

  const { userName = NO_VALUE, userImage } = useSelector(
    getTeamMemberNameAndImage
  );

  const [openNavItem, setOpenNavItem] = useState();

  const { ref: innerRef, height: innerHeight } = useHeightMeasurement();
  const { ref: outerRef, height: outerHeight } = useHeightMeasurement();

  const chosenBusinessName = useSelector(
    ({ authentication }) =>
      !!authentication.chosenBusiness && authentication.chosenBusiness.name
  );

  const dispatch = useDispatch();

  const onClickLogout = useCallback(() => {
    postLogOutMessage(instanceId);
    dispatch(serviceLogoutStart({}));
  }, [dispatch, instanceId]);

  const canAddBusiness = useSelector(
    ({ authentication }) =>
      authentication.access?.sections.settings_section &&
      authentication.access?.sections.settings_section.enabled &&
      authentication.access?.sections.settings_section.view
  );

  const canChooseBusiness = useSelector(
    ({ authentication }) =>
      !!authentication.user &&
      (hasMultipleBusinesses(authentication.user) ||
        authentication.user.is_superuser ||
        authentication.user.is_staff)
  );

  const filteredItems = useMemo(() => {
    return layout.map((item) => {
      return {
        ...item,
        children: item.children?.filter((child) => {
          if (child.id === DEFAULT_ITEM_IDS.CHANGE_BUSINESS) {
            return canChooseBusiness;
          } else if (child.id === DEFAULT_ITEM_IDS.ADD_BUSINESS) {
            return canAddBusiness;
          }
          return true;
        }),
      };
    });
  }, [canChooseBusiness, canAddBusiness, layout]);

  return isMobile ? (
    <CustomNavigationMobileWrapper
      height={innerHeight}
      expanded={expanded}
      className="Navigation"
      ref={outerRef}
      allowOverflow={innerHeight === outerHeight}
      data-qa-id="custom-navigation"
      data-qa-loading={loading}
    >
      <div ref={innerRef}>
        {filteredItems.map((item) => {
          return (
            <NavItem
              item={item}
              key={item.id}
              userName={userName}
              userImage={userImage}
              setOpen={() =>
                setOpenNavItem((existing) =>
                  existing === item.id ? null : item.id
                )
              }
              open={openNavItem === item.id}
              close={() => setOpenNavItem(null)}
              readOnly={readOnly}
              mobile
              chosenBusinessName={chosenBusinessName}
              onClickLogout={onClickLogout}
              parentWidth={parentWidth}
              dynamicItemConfig={dynamicItemConfigs[item.id]}
            />
          );
        })}
      </div>
    </CustomNavigationMobileWrapper>
  ) : (
    <CustomNavigationWrapper
      className="Navigation"
      data-qa-id="custom-navigation"
      data-qa-loading={loading}
    >
      {filteredItems.map((item, i) => {
        return (
          <NavItem
            item={item}
            key={item.id}
            userName={userName}
            userImage={userImage}
            setOpen={() =>
              setOpenNavItem((existing) =>
                existing === item.id ? null : item.id
              )
            }
            open={openNavItem === item.id}
            close={() => setOpenNavItem(null)}
            readOnly={readOnly}
            chosenBusinessName={chosenBusinessName}
            onClickLogout={onClickLogout}
            parentWidth={parentWidth}
            dynamicItemConfig={dynamicItemConfigs[item.id]}
            spacing={spacings[i]}
          />
        );
      })}
    </CustomNavigationWrapper>
  );
};

export default CustomNavigation;
